import { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutate } from 'restful-react'
import { env } from '@/services/EnvironmentVariables'
import { logger } from '@/services/Logger'
import { carmackClick, sleep, stripSemverVersion } from '@/utils'
import useRoomActions from '@/services/useRoomActions'
import { Button } from '@/UIComponents2/Button'
import DiagonalTabs from '@/components/DiagonalTabs'
import { AudioInputSelectionPanel } from '@/components/AudioInputSelectionPanel/AudioInputSelectionPanel'
import { ApiCreateRoomResponse, ApiRoomRequest } from '@/RoomContext'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import { BasicAVTab } from './internal/BasicAVTab'
import { useMediaStreamCleanup } from './internal/useMediaStreamCleanup'
import { SettingsTab } from './internal/SettingsTab'
import { useRoomStatePreload } from './internal/useRoomStatePreload'
import useMiscRoomActions from '@/services/useMiscRoomActions'
import useDimensionObserver from '@/services/useDimensionObserver'
import { useRecycleSessionId } from './internal/useRecycleSessionId'
import { Trans, useTranslation } from 'react-i18next'
import { PageLayout } from './internal/PageLayout'
import { SessionTypeInput } from '@/components/SessionTypeInput'
import { persistent } from '@/services/LocalStorage'
import { useUserContext } from '@/UserContext'
import { Dialog, DialogBody } from '@/components/Dialog'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'

const log = logger.createChildLogger({ name: 'HostSessionPage' })

export function HostSessionPage() {
    const { t } = useTranslation()
    const { loadRoom } = useRoomActions()
    const history = useHistory()
    useRoomStatePreload()
    useMediaStreamCleanup()
    useRecycleSessionId()

    const [roomRequest, setRoomRequest] = useState<ApiRoomRequest>({
        name: '',
        collaborators: [],
        allowJoiningFromCode: true,
        allowUserAutoJoin: false,
        createdFromAppVersion: stripSemverVersion(env.APP_VERSION) ?? '',
    })

    const {
        mutate: createRoom,
        loading: createRoomLoading,
        error: createRoomError,
    } = useMutate<ApiCreateRoomResponse, { code: string; message?: string; field?: string }>({
        verb: 'POST',
        path: `/v1/rooms`,
    })
    const [validationError, setValidationError] = useState<{ code: string; message?: string; field?: string }>()

    useEffect(() => {
        setValidationError(typeof createRoomError?.data !== 'string' ? createRoomError?.data : undefined)
    }, [createRoomError])

    const handleChange = (v: Partial<ApiRoomRequest>) => {
        setRoomRequest({ ...roomRequest, ...v })
        setValidationError(undefined)
    }

    const handleHostSession = async () => {
        if (createRoomLoading) {
            return
        }
        const result = await createRoom(roomRequest)
        loadRoom(result.room)
        await sleep(10)
        history.push('/collaborate')
    }

    const [width, setWidth] = useState(690)
    const { nodeRef: contentRef } = useDimensionObserver({
        onResize: (width) => {
            setWidth(width)
        },
    })

    const tabContentStyle = 'flex flex-col items-center pt-4 md:pt-8 lg:pt-12 pb-4 px-2 md:px-4 lg:px-10'

    return (
        <PageLayout
            rightContent={
                <HostMessage onHost={handleHostSession} disabled={createRoomLoading} onChange={handleChange} />
            }
            ref={contentRef}
        >
            <DiagonalTabs
                widthFitTabs
                childrenByTabIndex={[
                    <div className={tabContentStyle}>
                        <BasicAVTab />
                    </div>,
                    <div className={tabContentStyle}>
                        <div className="w-full min-h-[400px]">
                            <AudioInputSelectionPanel />
                        </div>
                    </div>,
                    <div className={'flex flex-col items-center py-4 md:py-8 lg:py-12'}>
                        <SettingsTab value={roomRequest} onChange={handleChange} error={validationError} />
                    </div>,
                ]}
                containerHeight={700}
                containerWidth={width}
                responsive
                tabs={[
                    { id: 'av', label: t('Audio & Video') },
                    { id: 'ad', label: t('Audio Devices') },
                    { id: 'settings', label: t('Settings') },
                ]}
                key="host-session-tabs"
            />
        </PageLayout>
    )
}

type JoinMessageProps = {
    onHost: () => void
    disabled: boolean
    onChange: (v: Partial<ApiRoomRequest>) => void
}
function HostMessage(props: JoinMessageProps) {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const { openSettingsModal } = useMiscRoomActions()

    const [permanent, setPermanent] = useState(false)
    const [permanentRoomError, setPermanentRoomError] = useState<string | null>(null)
    const [camp, setCamp] = useState(false)
    const [livestream, setLivestream] = useState(false)

    const canTogglePermanentRoom = useMemo(() => {
        const maxPermanentRoomCount = userState.profile?.policy?.maxPermanentRoomCount || 0
        if (!maxPermanentRoomCount || maxPermanentRoomCount < 0) {
            return false
        }

        return true
    }, [userState.profile])
    const canToggleCamp = userState.profile?.policy?.roomTierPolicy.allowSubrooms ?? false
    const canToggleLivestream = userState.profile?.policy?.allowCreateListeningSessions ?? false

    const handleSessionKindChange = (
        permanent: boolean,
        camp: boolean,
        livestream: boolean,
        roomDiff: Partial<ApiRoomRequest>
    ) => {
        if (permanent && !validateMaxPermanentRooms()) {
            setPermanentRoomError(t('You cannot create more permanent sessions.'))
            return
        }
        setPermanent(permanent)
        setCamp(camp)
        setLivestream(livestream)
        props.onChange(roomDiff)
    }

    const validateMaxPermanentRooms = () => {
        const permanentRooms = persistent.getArray<any>('permanentRooms') || []
        const maxPermanentRoomCount = userState.profile?.policy?.maxPermanentRoomCount || 0

        if (permanentRooms.length >= maxPermanentRoomCount) {
            return false
        } else {
            return true
        }
    }

    return (
        <>
            <div className="hidden md:block t-title py-[10px]">
                <Trans i18nKey="Host <1 /> Your Session" t={t}>
                    Host Session
                </Trans>
            </div>
            <Button
                variant="primary"
                className="relative"
                onMouseDown={carmackClick(props.onHost)}
                disabled={props.disabled}
            >
                {t('Start')}
                <RightArrowIcon className="absolute right-4" />
            </Button>
            <Button variant="glow" onClick={() => openSettingsModal('audio')}>
                {t('Detailed Settings')}
            </Button>

            <SessionTypeInput
                permanent={permanent}
                camp={camp}
                livestream={livestream}
                onChange={handleSessionKindChange}
                permanentLocked={!canTogglePermanentRoom}
                campLocked={!canToggleCamp}
                livestreamLocked={!canToggleLivestream}
                className="pt-5 self-center min-w-[150px]"
            />
            <Dialog open={!!permanentRoomError} onClose={() => setPermanentRoomError(null)}>
                <DialogBody>{permanentRoomError}</DialogBody>

                <DialogActionButtonsSection
                    rightButtonText={t('OK')}
                    onRightButtonClicked={() => setPermanentRoomError(null)}
                />
            </Dialog>
        </>
    )
}
